<template>
    <v-dialog
      v-model="dialogComputed"
      max-width="900px"
      scrollable
    >
        <v-card class="custom-positioned-dialog">
            <v-card-title>
                <v-icon icon="mdi-account-multiple-outline"></v-icon>
                Assign ({{ selectedSubItems.length }})

                <v-tooltip text="Select All" location="bottom">
                    <template v-slot:activator="{ props }">
                        <v-btn v-bind="props"
                               @click="selectAllUsers"
                               variant="text">
                            <v-icon>mdi-select-all</v-icon>
                        </v-btn>
                    </template>
                </v-tooltip>
                <v-tooltip text="Deselect All" location="bottom">
                    <template v-slot:activator="{ props }">
                        <v-btn v-bind="props"
                               @click="deselectAllUsers"
                               variant="text">
                            <v-icon>mdi-select-off</v-icon>
                        </v-btn>
                    </template>
                </v-tooltip>
            </v-card-title>
            <v-card-text>
                <v-alert type="info" variant="tonal" class="mb-4">
                    Select one or more users to assign to
                </v-alert>

                <v-list>
                    <v-list-item 
                                             v-for="user in teamUsersObj[localSelectedTeam]"
                                             :key="user.id"
                                             @click="toggleUserSelection(user.id)">
                                    <template v-slot:prepend>
                                        <v-checkbox v-model="selectedUsers"
                                                    :value="user.id"
                                                    density="compact"
                                                    hide-details
                                                    class="ma-0 pa-0 mr-2"
                                                    @click.stop></v-checkbox>
                                        <v-avatar :size="28"
                                                  class="mr-1">
                                            <v-img v-if="user.avatarUrl"
                                                   :src="getAvatarUrl(user.avatarUrl)"
                                                   alt="User Avatar"></v-img>
                                            <span v-else
                                                  class="d-flex justify-center align-center text-caption white--text"
                                                  :style="{
                                backgroundColor: getRandomColor(user.id),
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%'
                              }">
                                                {{ getInitials(user.displayName) }}
                                            </span>
                                        </v-avatar>
                                    </template>
                                    <v-list-item-title>{{ user.displayName }}</v-list-item-title>
                                    <v-list-item-subtitle>{{ user.roleTitle }}</v-list-item-subtitle>
                                    <template v-slot:append>
                                        <v-chip>{{ getUserItemCount(user.id) }}</v-chip>
                                    </template>
                    </v-list-item>
                </v-list>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="grey" text @click="closeDialog">
                    Cancel
                </v-btn>
                <v-btn color="primary" @click="assignTeam">
                    Assign
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="progressDialog" persistent max-width="300">
    <v-card>
      <v-card-text>
        Assigning items...
        <v-progress-linear
          indeterminate
          color="primary"
          class="mb-0"
        ></v-progress-linear>
      </v-card-text>
    </v-card>
  </v-dialog>
  </template>
  
  <script setup>
  import { ref, computed, defineProps, defineEmits, watch, onMounted, nextTick } from 'vue';
  import { useStore } from 'vuex';
  import { useAnalytics } from '@/utils/useAnalytics'

  const store = useStore();
  const { trackEvent } = useAnalytics()
  const conn = store.getters.getDBConnection;

  // Simple mobile detection
  const isMobile = ref(window.innerWidth < 960);
  const smAndDown = computed(() => isMobile.value);

  const props = defineProps(['dialog', 'selectedSubItems', 'selectedTeam', 'items']);
  const emit = defineEmits(['update:dialog', 'update:selectedSubItems', 'assign']);

  const dialogComputed = computed({
    get: () => props.dialog,
    set: (value) => emit('update:dialog', value)
  });

  // Load team users from DuckDB directly since Vuex store is not populated
  const teamUsersObj = ref({});

  const loadTeamUsersFromDB = async () => {
    if (Object.keys(teamUsersObj.value).length > 0) return; // Already loaded
    try {
      // Step 1: Load avatar URLs from users table (has Key-Value structure)
      const usersResult = await conn.query('SELECT Key, Value FROM users');
      const avatarMap = {}; // Map user ID -> avatar URL

      usersResult.toArray().forEach(row => {
        const rowData = row.toJSON();
        let teamUsers = rowData.Value || rowData.value;

        // Parse the JSON string to get array of users
        if (typeof teamUsers === 'string') {
          try {
            teamUsers = JSON.parse(teamUsers);
          } catch (e) {
            teamUsers = [];
          }
        }

        // Extract avatar URLs
        if (Array.isArray(teamUsers)) {
          teamUsers.forEach(u => {
            const id = u.Id || u.id;
            const avatarUrl = u.AvatarUrl || u.avatarUrl || '';
            if (id && avatarUrl && !avatarMap[id]) {
              avatarMap[id] = avatarUrl;
            }
          });
        }
      });

      // Step 2: Load team structure from team_users table
      const result = await conn.query('SELECT Id, Users FROM team_users');
      const teamUsers = {};
      result.toArray().forEach(row => {
        const team = row.toJSON();
        const teamId = team.Id || team.id;
        let users = team.Users || team.users || [];

        // Handle if Users is a string (JSON)
        if (typeof users === 'string') {
          try {
            users = JSON.parse(users);
          } catch (e) {
            users = [];
          }
        }

        // Convert Vector to array if needed
        if (users && typeof users.toArray === 'function') {
          users = users.toArray().map(u => typeof u.toJSON === 'function' ? u.toJSON() : u);
        }

        // Filter for current users and map to expected format
        if (Array.isArray(users)) {
          teamUsers[teamId] = users
            .filter(u => u.Current === true)
            .map(u => {
              const id = u.Id || u.id;
              const mappedUser = {
                id: id,
                displayName: u.DisplayName || u.displayName || '',
                avatarUrl: avatarMap[id] || '', // Get avatar from avatar map!
                roleTitle: u.RoleTitle || u.roleTitle || ''
              };
              return mappedUser;
            });
        }
      });
      teamUsersObj.value = teamUsers;
    } catch (error) {
      console.error('Error loading team users from DB:', error);
    }
  };
  const items = computed(() => store.getters.getItems);
  const settings = computed(() => store.getters.getSettings);

  const selectedSubItems = computed(() => props.selectedSubItems);
  
  const progressDialog = ref(false);
  
  const localSelectedTeam = ref(props.selectedTeam.length ? String(props.selectedTeam) : firstTeamId.value);
  const selectedUsers = ref([]);

  const getInitials = (name) => {
    const words = name.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    } else {
      return words[0].substring(0, 2).toUpperCase();
    }
  };
  
  const getRandomColor = (id) => {
    const colors = ['#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800', '#FF5722'];
    const index = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  const getAvatarUrl = (avatarUrl) => {
    if (!avatarUrl) {
      return '';
    }
    let finalUrl = '';
    // If the URL already includes a protocol, return as-is
    if (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://')) {
      finalUrl = avatarUrl;
    } else {
      // Otherwise, prepend https://
      finalUrl = 'https://' + avatarUrl;
    }
    return finalUrl;
  };


  const getUserItemCount = (userId) => {
    let count = 0;
    
    items.value.forEach(item => {
      if (item.subItems) {
        item.subItems.forEach(subItem => {
          if (subItem.assigneeUserIds && subItem.assigneeUserIds.includes(userId)) {
            count += 1;
          }
        });
      }
    });

    return count;
  };
  
  const closeDialog = () => {
    dialogComputed.value = false;
  };

  const firstTeamId = computed(() => {
  return Object.keys(teamUsersObj.value)[0] || '';
   });

  const assignTeam = async () => {
    progressDialog.value = true;

    const putObj = {
      teamId: String(localSelectedTeam.value),
      userIds: selectedUsers.value.map(String)
    };

    try {
      trackEvent('assignUsers', {
        action_type: 'button_click',
        apiUrl: store.state.apiClient.apiUrl,
        project_code: store.getters.getProjectCode,
        project_name: store.getters.getProjectName,
      })

      // Get the selected sub item IDs before any updates
      const selectedIds = [...selectedSubItems.value];

      // Use items from props (passed from parent) instead of filterContext
      const itemsToProcess = props.items || [];

      // Make API calls for each selected subItem
      for (const item of itemsToProcess) {
        for (const subItem of (item.subItems || [])) {
          if (selectedIds.includes(subItem.id)) {
            const itemType = item.type === 'IssueTemplate' ? 'issues' : 'forms';
            await store.state.apiClient.put(
              `/api/${settings.value.projectCode}/${itemType}/${subItem.id}/assignee`,
              putObj
            );
          }
        }
      }

      // Close progress dialog first
      progressDialog.value = false;

      // Emit events and close dialog using nextTick to ensure Vue completes updates
      await nextTick();

      // Emit the assign event with assignment data so parent can update items
      emit('assign', {
        teamId: putObj.teamId,
        userIds: putObj.userIds,
        subItemIds: selectedIds
      });
      emit('update:selectedSubItems', []);

      // Close dialog last
      closeDialog();

    } catch (error) {
      console.error("Error assigning items:", error);
      progressDialog.value = false;
    }
  };

  const toggleUserSelection = (userId) => {
    const index = selectedUsers.value.indexOf(userId);
    if (index === -1) {
      selectedUsers.value.push(userId);
    } else {
      selectedUsers.value.splice(index, 1);
    }
  };

  const selectAllUsers = () => {
    const teamUsers = teamUsersObj.value[localSelectedTeam.value] || [];
    selectedUsers.value = teamUsers.map(user => user.id);
  };

  const deselectAllUsers = () => {
    selectedUsers.value = [];
  };

  watch(() => props.selectedTeam, (newValue) => {
  localSelectedTeam.value = newValue.length ? String(newValue) : firstTeamId.value;
  }, { immediate: true });

  // Load team users when dialog opens
  watch(() => props.dialog, async (newValue) => {
    if (newValue) {
      await loadTeamUsersFromDB();
    }
  }, { immediate: true });

  onMounted(async () => {
    await loadTeamUsersFromDB();
  });

  </script>
  
  <style scoped>
  .v-list-group__items .v-list-item {
    padding-inline-start: 16px !important;
  }
  .v-list-item {
    cursor: pointer;
  }

  /* Only apply custom positioning on larger screens */
  @media (min-width: 960px) {
    .custom-positioned-dialog {
      position: relative;
      align-self: center;
      max-height: 90vh;
      margin-top: -9%;
    }
  }

  /* On mobile, ensure dialog is properly sized */
  @media (max-width: 959px) {
    .custom-positioned-dialog {
      margin: 16px;
      max-height: calc(100vh - 32px);
    }
  }
  </style>